import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useQuery } from '@tanstack/react-query';

const WINDMILL_URL = 'https://windmill.movatic.co/api/r/mixpanel/rental-events';
const ITEMS_PER_PAGE = 20;

export interface MixpanelEvent {
  event: string;
  properties: Record<string, any>;
}

const useMixpanelEvents = (rentalId: string | undefined) => {
  return useQuery<MixpanelEvent[]>({
    queryKey: ['mixpanel-events', rentalId],
    queryFn: async () => {
      if (!rentalId) return [];
      const res = await fetch(WINDMILL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rental_id: rentalId,
          from_date: '2024-01-01',
          to_date: new Date().toISOString().slice(0, 10),
        }),
      });
      if (!res.ok) throw new Error('Failed to fetch Mixpanel events');
      const data: MixpanelEvent[] = await res.json();
      return data.sort((a, b) => (b.properties.time ?? 0) - (a.properties.time ?? 0));
    },
    enabled: Boolean(rentalId),
    staleTime: 5 * 60 * 1000,
  });
};

const EVENT_COLORS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> =
  {
    'Rental Started': 'success',
    'Rental Completed': 'info',
    'End Rental Process Started': 'warning',
    'Location Check Result': 'default',
    'Live Activity Start Success': 'success',
    'Live Activity Start Attempt': 'default',
    'Live Activity Stop Attempt': 'default',
    'Live Activity Cleanup': 'default',
    'App Backgrounded': 'default',
    'App Foregrounded': 'default',
    'Pre-Rental Check Opened': 'info',
    'Pre-Rental Check Submitted': 'info',
    'Rental Process Completed': 'success',
    'Rental Rating Submitted': 'success',
    'Receipt Screen Closed': 'default',
  };

const EventRow = ({ event }: { event: MixpanelEvent }) => {
  const [open, setOpen] = useState(false);
  const color = EVENT_COLORS[event.event] ?? 'default';
  const ts = event.properties.time
    ? new Date(event.properties.time * 1000).toLocaleString()
    : null;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        px={2}
        py={1}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setOpen((v) => !v)}
      >
        {ts && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontFamily="monospace"
            sx={{ width: 160, flexShrink: 0 }}
          >
            {ts}
          </Typography>
        )}
        <Chip label={event.event} color={color} size="small" sx={{ fontSize: 11 }} />
        <Box flex={1} />
        <IconButton
          size="small"
          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Collapse in={open}>
        <Box px={2} pb={1.5} bgcolor="action.hover">
          <Stack spacing={0.3}>
            {Object.entries(event.properties).map(([k, v]) => (
              <Stack key={k} direction="row" spacing={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                  sx={{ width: 200, flexShrink: 0 }}
                >
                  {k}
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {JSON.stringify(v)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
      <Divider />
    </Box>
  );
};

const RentalMixpanelEvents = ({ rentalId }: { rentalId: string | undefined }) => {
  const { data: events = [], isLoading, isError } = useMixpanelEvents(rentalId);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);

  const eventTypes = ['all', ...Array.from(new Set(events.map((e) => e.event)))];
  const filtered = filter === 'all' ? events : events.filter((e) => e.event === filter);
  const paginated = filtered.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleFilter = (t: string) => {
    setFilter(t);
    setPage(0);
  };

  return (
    <Card>
      <CardHeader
        avatar={<TimelineIcon color="action" />}
        title="Mixpanel events"
        subheader={`${events.length} events for this rental`}
      />
      <Box px={2} pb={1.5}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {eventTypes.map((t) => (
            <Chip
              key={t}
              label={t === 'all' ? `all (${events.length})` : t}
              size="small"
              variant={filter === t ? 'filled' : 'outlined'}
              color={filter === t ? 'primary' : 'default'}
              onClick={() => handleFilter(t)}
              sx={{ fontSize: 11 }}
            />
          ))}
        </Stack>
      </Box>
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Could not load Mixpanel events.
          </Alert>
        )}
        {!isLoading && filtered.length === 0 && (
          <Typography color="text.secondary" variant="body2" textAlign="center" py={4}>
            No events found.
          </Typography>
        )}
        {paginated.map((event, i) => (
          <EventRow key={i} event={event} />
        ))}
        {totalPages > 1 && (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            py={1.5}
          >
            <IconButton
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {page + 1} / {totalPages} · {filtered.length} events
            </Typography>
            <IconButton
              size="small"
              disabled={page === totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RentalMixpanelEvents;
